/**
 * Pure H OS · Phase D · P0-1 · Decision Log
 *
 * A cross-module governance record — R&D go/no-go, finance approvals,
 * launch date lock-ins, supplier switches, etc. Foundational data feed
 * for Board Briefs, Owner Dashboards, and Reports.
 *
 * Reuses ONLY existing Pure H primitives: Card, SectionHeader,
 * SeverityBadge, TabBar, ListRow, Drawer. No new visual components.
 */
import { useMemo, useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { Card, SectionHeader, SeverityBadge, Button } from "../components/lib";
import { TabBar, ListRow, Drawer } from "../components/lib.overlay";
import { LinkedRefChip } from "../components/LinkedRefChip";
import { DocumentActions } from "../components/DocumentActions";
import { DECISIONS, Decision, DecisionOutcome } from "../mocks/decisionsData";
import { api } from "../services/api";
import { upsertLocal } from "../services/store";
import { useLiveTick } from "../lib/useLiveTick";

const OUTCOME_LABEL: Record<DecisionOutcome, string> = {
  go: "Go", hold: "Hold", kill: "Kill",
  reformulate: "Reformulate", approved: "Approved",
  declined: "Declined", deferred: "Deferred",
};

// Map an outcome to the existing SeverityBadge palette so we do not
// introduce a new visual component. "go"/"approved" → low (green), "hold"
// /"deferred"/"reformulate" → medium (amber), "kill"/"declined" → high (red).
function outcomeSeverity(o: DecisionOutcome): "high" | "medium" | "low" {
  if (o === "go" || o === "approved") return "low";
  if (o === "kill" || o === "declined") return "high";
  return "medium";
}

type Tab = "all" | Decision["module"] | "high";

export default function Decisions() {
  const [tab, setTab] = useState<Tab>("all");
  const [openId, setOpenId] = useState<string | null>(null);
  const [searchParams, setSearchParams] = useSearchParams();
  const [busy, setBusy] = useState<DecisionOutcome | null>(null);
  const [err, setErr] = useState<string | null>(null);
  useLiveTick();

  async function applyOutcome(outcome: DecisionOutcome) {
    if (!openId) return;
    setBusy(outcome);
    setErr(null);
    try {
      const updated = await api.patch("decisions", openId, { outcome }) as Partial<Decision> & { id: string };
      upsertLocal("decisions", updated);
    } catch (e) {
      setErr(e instanceof Error ? e.message : "Update failed");
    } finally {
      setBusy(null);
    }
  }

  // Deep-link support: /pureh/decisions?open=d-001 pre-opens that row's
  // drawer. Used by Board Briefs to jump to a specific decision.
  useEffect(() => {
    const q = searchParams.get("open");
    if (q && q !== openId && DECISIONS.some((d) => d.id === q)) {
      setOpenId(q);
    }
  }, [searchParams, openId]);

  // Keep the URL in sync when the drawer closes so a shared link
  // reflects the current state.
  useEffect(() => {
    if (!openId && searchParams.has("open")) {
      const next = new URLSearchParams(searchParams);
      next.delete("open");
      setSearchParams(next, { replace: true });
    }
  }, [openId, searchParams, setSearchParams]);

  const filtered = useMemo(() => {
    if (tab === "all") return DECISIONS;
    if (tab === "high") return DECISIONS.filter((d) => d.severity === "high");
    return DECISIONS.filter((d) => d.module === tab);
  }, [tab]);

  const current = openId ? DECISIONS.find((d) => d.id === openId) ?? null : null;

  return (
    <div className="space-y-6" data-testid="decisions-page">
      <Card testId="dec-filters">
        <TabBar<Tab>
          testId="dec-tabs"
          active={tab}
          onChange={setTab}
          tabs={[
            { key: "all",        label: `All ${DECISIONS.length}` },
            { key: "high",       label: "High" },
            { key: "R&D",        label: "R&D" },
            { key: "Finance",    label: "Finance" },
            { key: "Operations", label: "Operations" },
            { key: "Marketing",  label: "Marketing" },
            { key: "Launch",     label: "Launch" },
            { key: "Commerce",   label: "Commerce" },
            { key: "Executive",  label: "Executive" },
          ]}
        />
      </Card>

      <Card padded={false} testId="dec-list">
        <div className="p-5 flex items-start justify-between flex-wrap gap-3" style={{ borderBottom: "1px solid var(--ph-border-subtle)" }}>
          <div>
            <SectionHeader
              eyebrow="Decision Log"
              title={`${filtered.length} decision${filtered.length === 1 ? "" : "s"}`}
            />
            <div className="text-xs" style={{ color: "var(--ph-muted)" }}>
              Every governance record across the platform, most recent first.
            </div>
          </div>
          <DocumentActions docKey="decision-report" testId="dec-doc-actions" />
        </div>
        <div className="p-2">
          {filtered.map((d) => (
            <ListRow key={d.id} testId={`dec-${d.id}`}
              onClick={() => setOpenId(d.id)}
              label={
                <span className="flex items-center gap-2">
                  <span className="uppercase tracking-[0.14em] text-[10px]" style={{ color: "var(--ph-muted)" }}>
                    {OUTCOME_LABEL[d.outcome]}
                  </span>
                  <span>{d.title}</span>
                </span>
              }
              sublabel={
                <>
                  {d.module} · {d.decidedBy} · {d.dateRelative}
                  {typeof d.budgetImpactEGP === "number" && d.budgetImpactEGP !== 0 ? (
                    <> · EGP {Math.abs(d.budgetImpactEGP).toLocaleString()}{d.budgetImpactEGP < 0 ? " reclaimed" : ""}</>
                  ) : null}
                </>
              }
              trailing={<SeverityBadge severity={outcomeSeverity(d.outcome)} />}
            />
          ))}
        </div>
      </Card>

      <Drawer open={!!current} onClose={() => setOpenId(null)} testId="dec-drawer"
              title={current ? current.title : ""}>
        {current && (
          <div className="space-y-6">
            <SectionHeader
              eyebrow={`${current.module} · ${OUTCOME_LABEL[current.outcome]}`}
              title={`Decided by ${current.decidedBy}`}
            />
            {/* Single-record Decision Report PDF · ORVAN branded */}
            <div className="pb-3 -mt-2" style={{ borderBottom: "1px solid var(--ph-border-subtle)" }}>
              <DocumentActions docKey="decision-report" recordId={current.id} testId={`dec-single-${current.id}`} compact />
            </div>
            <div className="text-xs -mt-4" style={{ color: "var(--ph-muted)" }}>
              Requested by {current.requestedBy} · {current.dateRelative}
            </div>

            <div className="text-sm leading-relaxed" style={{ color: "var(--ph-text)" }}>
              {current.rationale}
            </div>

            <div className="grid grid-cols-3 gap-4">
              <div>
                <div className="text-[10px] uppercase tracking-[0.16em]" style={{ color: "var(--ph-muted)" }}>Severity</div>
                <div className="mt-2"><SeverityBadge severity={current.severity} /></div>
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-[0.16em]" style={{ color: "var(--ph-muted)" }}>Budget impact</div>
                <div className="mt-1 text-sm" style={{ color: "var(--ph-text)" }}>
                  {typeof current.budgetImpactEGP === "number"
                    ? `EGP ${current.budgetImpactEGP.toLocaleString()}`
                    : "—"}
                </div>
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-[0.16em]" style={{ color: "var(--ph-muted)" }}>AI confidence</div>
                <div className="mt-1 text-sm" style={{ color: "var(--ph-text)" }}>
                  {typeof current.aiConfidence === "number"
                    ? `${Math.round(current.aiConfidence * 100)}%`
                    : "—"}
                </div>
              </div>
            </div>

            {current.linkedTo && (
              <div>
                <div className="text-[10px] uppercase tracking-[0.16em]" style={{ color: "var(--ph-muted)" }}>Linked entity</div>
                <div className="mt-1 text-sm" style={{ color: "var(--ph-text)" }}>
                  {current.linkedKind ?? "entity"} · <LinkedRefChip id={current.linkedTo} kind={current.linkedKind} testId={`dec-linked-${current.id}`} />
                </div>
              </div>
            )}

            <div className="pt-4" style={{ borderTop: "1px solid var(--ph-border-subtle)" }}>
              <div className="text-[10px] uppercase tracking-[0.16em] mb-2" style={{ color: "var(--ph-muted)" }}>
                Owner actions · current outcome <span className="font-semibold" style={{ color: "var(--ph-text)" }}>{OUTCOME_LABEL[current.outcome]}</span>
              </div>
              {err && <div className="text-xs mb-2" style={{ color: "var(--ph-danger)" }} data-testid="dec-action-error">{err}</div>}
              <div className="flex flex-wrap gap-2">
                <Button variant="primary"   disabled={busy !== null} onClick={() => applyOutcome("approved")} testId="dec-action-approve">
                  {busy === "approved" ? "Approving…" : "Approve"}
                </Button>
                <Button variant="secondary" disabled={busy !== null} onClick={() => applyOutcome("go")} testId="dec-action-go">
                  {busy === "go" ? "Marking…" : "Go"}
                </Button>
                <Button variant="secondary" disabled={busy !== null} onClick={() => applyOutcome("hold")} testId="dec-action-hold">
                  {busy === "hold" ? "Holding…" : "Hold"}
                </Button>
                <Button variant="secondary" disabled={busy !== null} onClick={() => applyOutcome("deferred")} testId="dec-action-defer">
                  {busy === "deferred" ? "Deferring…" : "Defer"}
                </Button>
                <Button variant="secondary" disabled={busy !== null} onClick={() => applyOutcome("declined")} testId="dec-action-decline">
                  {busy === "declined" ? "Declining…" : "Decline"}
                </Button>
                <Button variant="secondary" disabled={busy !== null} onClick={() => applyOutcome("kill")} testId="dec-action-kill">
                  {busy === "kill" ? "Killing…" : "Kill"}
                </Button>
              </div>
            </div>
          </div>
        )}
      </Drawer>
    </div>
  );
}
